/**
 * The Delivery tab: how and when mail goes out, for the whole tenant. Every card
 * here applies immediately; none of it waits for the grid's Save.
 *
 * The send limit, supplier exception and scorecard cards hold unapplied edits of
 * their own, so the screen keeps this tab mounted while it is hidden and through
 * the reload a row Save runs. Only a change of company remounts it, which is what
 * stops an edit typed for one company reaching another. The error state still
 * replaces it, and of these edits only the quiet-hours draft outlives that.
 *
 * Owner-first routing has no card of its own, so its notice sits directly under
 * the Daily digest card: it is about the same once-a-day email.
 *
 * @link ./NotificationSetupTab for the props' origin.
 */
import type { NotificationSettingsUpdate } from '../../../../lib/api';
import { Alert, AlertDescription, AlertTitle } from '../../../ui/alert';
import { MailX, Route } from 'lucide-react';
import type { PlatformSettings } from '../types';
import { NotificationDefaultFrequency } from './NotificationDefaultFrequency';
import { NotificationDeliveryCard } from './NotificationDeliveryCard';
import { NotificationDigestCard } from './NotificationDigestCard';
import { NotificationSendBudgetCard } from './NotificationSendBudgetCard';
import { SupplierScorecardCard } from './SupplierScorecardCard';
import { EMAIL_STOPPED_TEXT, EMAIL_STOPPED_TITLE, type OneDailyDelivery } from './types';
import type { UseNotificationSetupResult } from './useNotificationSetup';
import type { UseQuietHoursDraftResult } from './useQuietHoursDraft';

interface NotificationDeliveryTabProps {
  setup: UseNotificationSetupResult;
  quietHours: UseQuietHoursDraftResult;
  /** CR-8217: refused to anyone but the customer side. */
  canSetInstallSwitches: boolean;
  /** Batched mail is really going out: Digest on, and email not stopped. */
  batchedMail: boolean;
  /** What the one daily digest does for this tenant right now. */
  digestDelivery: OneDailyDelivery;
  /** The one daily digest is really going out. */
  digestActive: boolean;
  platformSettings: PlatformSettings;
  savingPlatform: boolean;
  onPlatformSettingsChange: (settings: PlatformSettings) => void;
  onSavePlatform: () => void;
}

export function NotificationDeliveryTab({
  setup,
  quietHours,
  canSetInstallSwitches,
  batchedMail,
  digestDelivery,
  digestActive,
  platformSettings,
  savingPlatform,
  onPlatformSettingsChange,
  onSavePlatform,
}: NotificationDeliveryTabProps) {
  const baselineRouting = setup.catalogue?.baselineRouting ?? false;
  const oneDailyDigest = setup.catalogue?.oneDailyDigest ?? false;
  // Not "by bell" with the Bell off: nothing reaches anyone that way then.
  const byBell = oneDailyDigest && setup.master.bellEnabled;
  // The daily mail rides on the bell row, so without a bell the email stays per event.
  const noBellNoDaily = setup.master.emailsEnabled && !setup.master.bellEnabled;
  const departmentDelivery = noBellNoDaily
    ? ' gets an email per event while the Bell is off'
    : batchedMail
      ? ' gets one email a day instead of one per event'
      : ' gets the bell only while digest emails are off, never an email per event';
  const applySettings = (patch: NotificationSettingsUpdate, message: string) => {
    void setup.saveSettings(patch, message);
  };

  return (
    <>
      {!setup.master.emailsEnabled && (
        <Alert variant="destructive">
          <MailX className="h-4 w-4" />
          <AlertTitle>{EMAIL_STOPPED_TITLE}</AlertTitle>
          <AlertDescription>{EMAIL_STOPPED_TEXT}</AlertDescription>
        </Alert>
      )}

      <NotificationDigestCard
        master={setup.master}
        canConfigure={setup.canConfigure}
        canSetInstallSwitches={canSetInstallSwitches}
        saving={setup.savingSettings}
        oneDailyDigest={oneDailyDigest}
        digestActive={digestActive}
        batchedMail={batchedMail}
        onChange={applySettings}
      />

      {baselineRouting && (
        <Alert>
          <Route className="h-4 w-4" />
          <AlertTitle>Owner-first routing is on</AlertTitle>
          <AlertDescription>
            Supplier events go to the order line&apos;s buyer or the NCR&apos;s owner
            {byBell ? ' by bell as they happen' : ' as they happen'}
            {digestActive && ', and by email in the daily digest'}
            . When there is none, the purchasing or quality department
            {departmentDelivery}
            , and so does anyone else an event reaches without a named owner. Setting a
            notification&apos;s own frequency, or taking its bell away, changes that for it.
          </AlertDescription>
        </Alert>
      )}

      <NotificationDefaultFrequency
        value={setup.master.defaultFrequency}
        choices={setup.catalogue?.frequencyChoices ?? []}
        summary={setup.frequencySummary}
        disabled={!setup.canConfigure || setup.savingSettings}
        others={baselineRouting && setup.master.bellEnabled ? (batchedMail ? 'daily' : 'bell') : null}
        oneDaily={digestDelivery}
        onChange={(frequency) => void setup.setDefaultFrequency(frequency)}
      />

      <NotificationDeliveryCard
        quietHours={quietHours}
        canConfigure={setup.canConfigure && canSetInstallSwitches}
        saving={setup.savingSettings}
        onChange={applySettings}
        systemAlertEmails={platformSettings.systemAlertEmails}
        onSystemAlertEmailsChange={(value) =>
          onPlatformSettingsChange({ ...platformSettings, systemAlertEmails: value })
        }
        onSaveSystemAlertEmails={onSavePlatform}
        savingPlatform={savingPlatform}
      />

      <NotificationSendBudgetCard
        master={setup.master}
        canConfigure={setup.canConfigure}
        saving={setup.savingSettings}
        onChange={applySettings}
        company={setup.catalogue?.companyId ?? undefined}
      />

      <SupplierScorecardCard
        canConfigure={setup.canConfigure}
        emailsEnabled={setup.master.emailsEnabled}
      />
    </>
  );
}
